import { useDebounceFn } from '@vueuse/core'
import { useAssetAbTests, useCoreConfig, useDayjs, useRouter, useTracking } from '#imports'
import type { ExperimentValueUnion } from '@core/types'
import type { GameTrackingOrigin } from '@game/types'
import { hasTrackableUrl } from '@game/utils/tracking'

export interface GameImpressionEntry {
    listSlug: string
    gameSlug: string
    /**
     * Surface the tile was exposed on; defaults to `gametile`. Set it when the
     * list is not a lobby tile grid (e.g. the playgame selector), so the rows
     * can be separated from lobby exposure without parsing slugs.
     */
    origin?: GameTrackingOrigin
    /** Owning route of the list; defaults to the live route at record time. */
    url?: string
    row?: number
    col?: number
    index?: number
    userId?: number | string | null
}

interface GameImpressionRecord extends GameImpressionEntry {
    origin: GameTrackingOrigin
    url: string
    seenAt: string
    /** Asset A/B bucket, present only while the game is under test (`a` = control). */
    variant?: ExperimentValueUnion
}

export interface UseGameImpressionsReturn {
    enabled: boolean
    record: (entry: GameImpressionEntry) => void
    flush: () => void
}

const FLUSH_DEBOUNCE_MS = 2500
const FLUSH_MAX_WAIT_MS = 10_000

// Module-level singleton, not createSharedComposable: the dedupe set and any
// pending debounced flush must survive windows with no mounted game lists
// (e.g. the search overlay closing and reopening).
let state: UseGameImpressionsReturn | null = null

const disabled: UseGameImpressionsReturn = {
    enabled: false,
    record: () => {},
    flush: () => {},
}

/**
 * Batched game-tile impression tracking: collects one entry per (list, game)
 * per page-view and ships the buffer as a single `game-impressions` event.
 */
export function useGameImpressions(): UseGameImpressionsReturn {
    if (import.meta.server) return disabled
    if (!useCoreConfig().trackingService?.enabled) return disabled

    return state ??= createState()
}

function createState(): UseGameImpressionsReturn {
    const { trackEvent } = useTracking()
    const { trackingMeta } = useAssetAbTests()
    const router = useRouter()
    const dayjs = useDayjs()

    const seen = new Set<string>()
    let buffer: GameImpressionRecord[] = []

    function flush() {
        if (!buffer.length) return

        const meta_data = buffer
        buffer = []

        void trackEvent({
            event_name: 'game-impressions',
            event_type: 'Load',
            meta_data,
        })
    }

    const debouncedFlush = useDebounceFn(flush, FLUSH_DEBOUNCE_MS, { maxWait: FLUSH_MAX_WAIT_MS })

    function record(entry: GameImpressionEntry) {
        const origin = entry.origin ?? 'gametile'
        const url = entry.url ?? router.currentRoute.value.path

        if (!hasTrackableUrl(url, origin, 'impression')) return

        const key = `${origin}:${entry.listSlug}:${entry.gameSlug}`
        if (seen.has(key)) return
        seen.add(key)

        buffer.push({
            ...entry,
            ...trackingMeta(entry.gameSlug),
            origin,
            url,
            seenAt: dayjs().toISOString(),
        })

        void debouncedFlush()
    }

    // Path change = new page-view: flush and reset dedupe. Query-only navs
    // (playgame selector/deposit toggles) are the same page-view.
    router.afterEach((to, from) => {
        if (to.path === from.path) return

        flush()
        seen.clear()
    })

    // Best-effort flush when the page is backgrounded or closed — @cego/tracking
    // sends via fetch without keepalive, so hard-unload delivery isn't guaranteed.
    // Page-lifetime listeners; the dedupe set survives in case the user returns.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush()
    })
    window.addEventListener('pagehide', flush)

    return { enabled: true, record, flush }
}
